import { BookOpen, Cloud, Layers, Sparkles, TrendingUp } from "lucide-react"
import { motion, useInView } from "motion/react"
import { useRef } from "react"

import { Logo } from "@/brand/components/logo"
import { Button } from "@/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardTitle } from "@/ui/card"
import { Muted } from "@/ui/typography"
import { cn } from "@/ui/utils"

const enter = { opacity: 0, y: 20 }
const visible = { opacity: 1, y: 0 }

function CourseJourneyStackRing({
  className,
  orbitActive,
}: {
  className?: string
  orbitActive: boolean
}) {
  const items = [
    {
      name: "PageZERO",
      node: <Logo className="size-8 sm:size-12" />,
    },
    {
      name: "Cloudflare",
      node: (
        <img
          src="https://cdn.simpleicons.org/cloudflare/F38020"
          alt=""
          width={48}
          height={48}
          loading="lazy"
          decoding="async"
          className="size-8 sm:size-12"
          aria-hidden
        />
      ),
    },
    {
      name: "Cursor",
      node: (
        <img
          src="https://cdn.simpleicons.org/cursor/14120B"
          alt=""
          width={48}
          height={48}
          loading="lazy"
          decoding="async"
          className="size-8 sm:size-12 dark:invert"
          aria-hidden
        />
      ),
    },
  ] as const

  return (
    <div
      className={cn(
        "relative mx-auto size-64 [--orbit-radius:100px] sm:size-112 sm:[--orbit-radius:150px]",
        className,
      )}
      role="img"
      aria-label="PageZERO, Cloudflare, and Cursor—the core stack for this course"
    >
      {/* Connecting ring */}
      <div
        className="border-border absolute rounded-full border-2 border-dashed"
        style={{
          width: "calc(var(--orbit-radius) * 2)",
          height: "calc(var(--orbit-radius) * 2)",
          left: "calc(50% - var(--orbit-radius))",
          top: "calc(50% - var(--orbit-radius))",
        }}
      />

      {/* Center element */}
      <div className="bg-primary/10 text-primary absolute top-1/2 left-1/2 flex size-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full sm:size-18">
        <BookOpen className="size-6 sm:size-8" aria-hidden />
      </div>

      <div className={cn("absolute inset-0", orbitActive && "animate-orbit will-change-transform")}>
        {items.map((item, i) => {
          const angleDeg = i * 120 - 90
          const rad = (angleDeg * Math.PI) / 180
          const x = Math.cos(rad)
          const y = Math.sin(rad)
          return (
            <div
              key={item.name}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{
                marginLeft: `calc(var(--orbit-radius) * ${x})`,
                marginTop: `calc(var(--orbit-radius) * ${y})`,
              }}
            >
              <div
                className={cn(
                  "direction-reverse flex size-16 items-center justify-center rounded-full border border-border/70 bg-background shadow-sm ring-1 ring-border/40 sm:size-24",
                  orbitActive && "animate-orbit will-change-transform",
                )}
              >
                {item.node}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const pillars = [
  {
    icon: Sparkles,
    title: "Code with AI agents",
    description:
      "Scaffold features in minutes with Cursor and AI agents, using the prompts, guardrails, and review habits that keep quality high.",
  },
  {
    icon: Cloud,
    title: "Master Cloudflare",
    description:
      "See how to build full-stack apps on the Cloudflare platform, then use the tooling and dashboard to deploy and scale without guessing.",
  },
  {
    icon: Layers,
    title: "Build on PageZERO",
    description:
      "Learn to take full advantage of PageZERO, then build features on a foundation that's already tested, typed, and wired together.",
  },
  {
    icon: TrendingUp,
    title: "Get to paying customers",
    description:
      "Finish the course with a working app, a payments flow, and a path to your first paying customer. No toy demos.",
  },
] as const

export function CourseSection() {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-80px" })
  const orbitInView = useInView(ref, { margin: "-80px" })

  return (
    <div ref={ref} className="relative mx-auto max-w-6xl">
      <div className="pointer-events-none absolute inset-0 overflow-clip" aria-hidden>
        <div className="bg-primary/10 dark:bg-primary/15 absolute top-0 -left-20 size-80 rounded-full blur-3xl" />
        <div className="bg-chart-2/15 dark:bg-chart-2/20 absolute -right-16 bottom-0 size-72 rounded-full blur-3xl" />
      </div>

      <Card className="bg-card/70 dark:bg-card/40 relative gap-0 overflow-hidden rounded-2xl border py-0 shadow-lg backdrop-blur-sm">
        <div className="via-primary/25 absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent to-transparent" />

        <CardContent className="grid gap-10 py-10 sm:px-10 lg:grid-cols-2">
          <div className="flex min-w-0 flex-col justify-center">
            <motion.div
              className="flex justify-center lg:justify-start"
              initial={enter}
              animate={isInView ? visible : enter}
              transition={{
                duration: 0.5,
                delay: 0.06,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <CourseJourneyStackRing orbitActive={orbitInView} />
            </motion.div>
          </div>

          <div className="grid min-w-0 gap-4 sm:grid-cols-2">
            {pillars.map((pillar, i) => {
              const Icon = pillar.icon
              return (
                <motion.div
                  key={pillar.title}
                  className="h-full min-h-0"
                  initial={enter}
                  animate={isInView ? visible : enter}
                  transition={{
                    duration: 0.5,
                    delay: 0.12 + 0.06 * i,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  <Card className="group bg-muted/20 hover:border-primary/25 hover:bg-muted/35 h-full min-h-0 gap-0 py-0 transition-colors">
                    <CardContent className="flex min-h-0 flex-1 flex-col gap-3 px-5 py-5">
                      <div className="bg-primary/10 text-primary flex size-10 items-center justify-center rounded-lg transition-transform group-hover:scale-105">
                        <Icon className="size-5" aria-hidden />
                      </div>
                      <CardTitle className="text-base leading-snug">{pillar.title}</CardTitle>
                      <CardDescription className="leading-relaxed text-pretty">
                        {pillar.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>
              )
            })}
          </div>
        </CardContent>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : undefined}
          transition={{ delay: 0.45, duration: 0.5 }}
        >
          <CardFooter className="border-border/60 bg-muted/15 flex-col items-center justify-center gap-5 border-t py-8">
            <div className="max-w-md text-center">
              <p className="text-foreground font-medium">
                Join the newsletter and get 20% off at launch
              </p>
              <Muted className="mt-1">Be the first to know when the series drops.</Muted>
            </div>
            <Button asChild size="lg">
              <a href="#newsletter">Join the newsletter</a>
            </Button>
          </CardFooter>
        </motion.div>
      </Card>
    </div>
  )
}
